<template>
  <div class="left">
    <div class="panel pv-panel-site">
      <div class="panel-title">站点总览 · 济源</div>
      <div class="num-card">
        <div class="num-value pv-site-pair">
          {{ onlineSites }}<span class="pv-num-slash">/</span>{{ totalSites }}
        </div>
        <div class="num-label">在线站点 / 总站点</div>
      </div>
      <div class="num-row" style="margin-top: 6px">
        <div class="num-item green">
          <div class="val">{{ deviceTotalText }}</div>
          <div class="lbl">设备总数</div>
        </div>
        <div class="num-item">
          <div class="val">{{ deviceOnlineRateText }}</div>
          <div class="lbl">设备在线率</div>
        </div>
        <div class="num-item orange">
          <div class="val">{{ pendingAlarms }}</div>
          <div class="lbl">未处理告警</div>
        </div>
      </div>
    </div>

    <div class="panel pv-panel-scroll-body">
      <div class="panel-title">🏆 能源自给率排名</div>
      <div v-if="!rankList.length" class="pv-list-empty">暂无排名数据</div>
      <div
        v-else
        class="pv-list-scroll-wrap"
        :class="{ 'pv-list-scroll-wrap--manual': !rankScrollAnimate }"
      >
        <ul
          class="rank-list pv-list-scroll"
          :class="{ 'pv-list-scroll--animate': rankScrollAnimate }"
        >
          <li
            v-for="(row, idx) in rankScrollRows"
            :key="row._scrollKey"
          >
            <span class="rank-num" :class="rankNumClass(rankRowIndex(idx))">{{
              rankRowIndex(idx) + 1
            }}</span>
            <span class="rank-name">{{ row.spaceName || "—" }}</span>
            <div class="rank-bar" :class="rankBarClass(rankRowIndex(idx))">
              <div class="rank-bar-inner" :style="{ width: row.barWidth + '%' }" />
            </div>
            <span class="rank-val">{{ row.pctStr }}</span>
          </li>
        </ul>
      </div>
      <div class="pv-rank-foot">
        平均 <span class="pv-foot-avg">{{ energyAvg }}</span> ｜最高
        <span class="pv-foot-max">{{ energyMax }}</span> ｜最低
        <span class="pv-foot-min">{{ energyMin }}</span>
      </div>
    </div>

    <div class="panel pv-panel-scroll-body">
      <div class="panel-title">
        🔔 实时告警
        <span class="pv-alarm-count-hint">{{ pendingAlarms }}条待处理</span>
      </div>
      <div
        class="pv-list-scroll-wrap"
        :class="{ 'pv-list-scroll-wrap--manual': !alarmScrollAnimate }"
      >
        <ul
          class="alarm-list pv-list-scroll"
          :class="{ 'pv-list-scroll--animate': alarmScrollAnimate }"
        >
          <li
            v-if="!alarms.length"
            class="pv-alarm-empty"
          >
            ✅ 所有告警已处理
          </li>
          <li
            v-for="(a, i) in alarmScrollRows"
            :key="a._scrollKey"
            class="pv-alarm-li"
            @click="onAlarmClick(i)"
          >
            <span class="alarm-dot" :class="a.level" />
            <span class="alarm-msg">{{ a.station }} - {{ a.msg }}</span>
            <span class="alarm-time">{{ a.time }}</span>
          </li>
        </ul>
      </div>
      <div
        style="margin-top: auto; padding-top: 6px; border-top: 1px solid rgba(0,212,255,0.08); display: flex; justify-content: space-between; font-size: 10px"
      >
        <span style="color: rgba(0,230,118,0.6)">✅ 已消警：{{ clearedCount }}条</span>
        <a
          href="#"
          style="color: rgba(0,212,255,0.4); text-decoration: none"
          @click.prevent="$emit('show-log')"
          >📋 消警记录</a
        >
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { gatewayList } from "@/network/device-gateway";
import { countDeviceStatusByTag } from "@/network/device-info";
import { getAlarmByCommitTimeAsc } from "@/network/alarm-realtime";
import { energySelfSufficiencyRate } from "@/network/photovoltaic";

function parsePercentNumber(v) {
  if (v == null || v === "") return 0;
  const n = Number(v);
  if (Number.isNaN(n)) return 0;
  if (n > 0 && n <= 1) return Math.round(n * 1000) / 10;
  return Math.round(n * 10) / 10;
}

function formatPercentDisplay(v) {
  const n = parsePercentNumber(v);
  return (Number.isInteger(n) ? String(n) : n.toFixed(1)) + "%";
}

function mapAlarmLevel(item) {
  const name = (item.levelName || "").toString();
  const id = String(item.levelId != null ? item.levelId : "");
  if (/紧急|致命|严重/i.test(name) || id === "4" || id === "1") return "red";
  if (/重要|高/i.test(name) || id === "3" || id === "2") return "orange";
  return "yellow";
}

/** 与 App.vue 告警通知展示一致：设备名 + 测点名 */
function formatAlarmStation(item) {
  const device = item.deviceName || item.spaceName || item.space || "—";
  const point = item.pointName ? " " + item.pointName : "";
  return device + point;
}

/** 在接口原始字段上补充大屏展示字段，供消警时传 id / operationTxt / operator */
function mapRealtimeRow(item) {
  const t = item.createTime
    ? moment(item.createTime).format("MM-DD HH:mm")
    : item.time || "—";
  return {
    ...item,
    id: item.id,
    deviceName: item.deviceName,
    pointName: item.pointName,
    msg: item.msg || "—",
    createTime: item.createTime,
    levelName: item.levelName,
    levelColor: item.levelColor,
    isConfirm: item.isConfirm,
    station: formatAlarmStation(item),
    time: t,
    level: mapAlarmLevel(item),
  };
}

export default {
  name: "PvLeftColumn",
  props: {
    alarms: {
      type: Array,
      default: () => [],
    },
    /** 本页会话内消警条数（与 App 消警后刷新实时列表一致，由父级维护记录） */
    clearedCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      onlineSites: 0,
      totalSites: 0,
      deviceTotal: 0,
      deviceOnline: 0,
      rankList: [],
      energyAvg: "—",
      energyMax: "—",
      energyMin: "—",
      pollTimer: null,
    };
  },
  computed: {
    rankScrollAnimate() {
      return this.rankList.length > 4;
    },
    rankScrollRows() {
      const rows = this.rankList;
      if (!rows.length) return [];
      const list = this.rankScrollAnimate ? [...rows, ...rows] : rows;
      return list.map((r, idx) => ({
        ...r,
        _scrollKey: `${r.spaceId}-${idx}`,
      }));
    },
    alarmScrollAnimate() {
      return this.alarms.length > 3;
    },
    alarmScrollRows() {
      const rows = this.alarms;
      if (!rows.length) return [];
      const list = this.alarmScrollAnimate ? [...rows, ...rows] : rows;
      return list.map((a, idx) => ({
        ...a,
        _scrollKey: a.id != null ? `a-${a.id}-${idx}` : `i-${idx}`,
      }));
    },
    pendingAlarms() {
      return this.alarms.length;
    },
    deviceTotalText() {
      if (!this.deviceTotal) return "0";
      return this.deviceTotal.toLocaleString("zh-CN");
    },
    deviceOnlineRateText() {
      if (!this.deviceTotal) return "—";
      const p = (this.deviceOnline / this.deviceTotal) * 100;
      return (Math.round(p * 10) / 10).toFixed(1) + "%";
    },
  },
  mounted() {
    this.loadAll();
    this.pollTimer = setInterval(() => this.loadAll(), 30000);
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  methods: {
    rankRowIndex(idx) {
      const n = this.rankList.length;
      return n ? idx % n : 0;
    },
    onAlarmClick(idx) {
      const n = this.alarms.length;
      if (!n) return;
      this.$emit("alarm-click", idx % n);
    },
    rankNumClass(idx) {
      if (idx === 0) return "gold";
      if (idx === 1) return "silver";
      if (idx === 2) return "bronze";
      return "normal";
    },
    rankBarClass(idx) {
      if (idx === 0) return "green";
      return idx % 2 === 1 ? "blue" : "orange";
    },
    loadGateways() {
      return gatewayList()
        .then((res) => {
          if (!res || res.code !== 0) return;
          const list = res.data;
          if (!Array.isArray(list)) {
            this.onlineSites = 0;
            this.totalSites = 0;
            this.$emit("sites-loaded", { online: 0, total: 0 });
            return;
          }
          this.totalSites = list.length;
          this.onlineSites = list.filter((g) => g && g.status === 1).length;
          this.$emit("sites-loaded", {
            online: this.onlineSites,
            total: this.totalSites,
          });
        })
        .catch(() => {
          this.onlineSites = 0;
          this.totalSites = 0;
        });
    },
    loadDeviceByTag() {
      return countDeviceStatusByTag()
        .then((res) => {
          if (!res || res.code !== 0 || !res.data || !res.data.tag) {
            this.deviceTotal = 0;
            this.deviceOnline = 0;
            return;
          }
          const item = res.data;
          let total = 0;
          let online = 0;
          for (let i = 0; i < item.tag.length; i++) {
            const on = Number(item.online[i]) || 0;
            const off = Number(item.offline[i]) || 0;
            online += on;
            total += on + off;
          }
          this.deviceTotal = total;
          this.deviceOnline = online;
        })
        .catch(() => {
          this.deviceTotal = 0;
          this.deviceOnline = 0;
        });
    },
    /** 与 App.vue getAlarmData 相同接口 */
    loadRealtimeAlarms() {
      return getAlarmByCommitTimeAsc()
        .then((res) => {
          const list = res && res.data ? res.data : [];
          const raw = Array.isArray(list) ? list : [];
          this.$emit("alarms-loaded", raw.map(mapRealtimeRow));
        })
        .catch(() => {});
    },
    loadEnergyRank() {
      return energySelfSufficiencyRate()
        .then((res) => {
          if (!res || res.code !== 0 || !res.data) {
            this.rankList = [];
            return;
          }
          const d = res.data;
          this.energyAvg = d.avg != null && d.avg !== "" ? String(d.avg) : "—";
          this.energyMax = d.max != null && d.max !== "" ? String(d.max) : "—";
          this.energyMin = d.min != null && d.min !== "" ? String(d.min) : "—";
          const rows = Array.isArray(d.spaceEnergySelfSufficiencyRates)
            ? d.spaceEnergySelfSufficiencyRates.slice()
            : [];
          rows.sort(
            (a, b) =>
              parsePercentNumber(b.selfSufficiencyRate) -
              parsePercentNumber(a.selfSufficiencyRate)
          );
          const maxPct = rows.length
            ? Math.max(
                ...rows.map((r) => parsePercentNumber(r.selfSufficiencyRate)),
                1
              )
            : 1;
          this.rankList = rows.map((r) => {
            const pct = parsePercentNumber(r.selfSufficiencyRate);
            return {
              spaceId: r.spaceId,
              spaceName: r.spaceName,
              pctStr: formatPercentDisplay(r.selfSufficiencyRate),
              barWidth: Math.min(100, Math.round((pct / maxPct) * 100)),
            };
          });
        })
        .catch(() => {
          this.rankList = [];
        });
    },
    loadAll() {
      this.loadGateways();
      this.loadDeviceByTag();
      this.loadRealtimeAlarms();
      this.loadEnergyRank();
    },
    /** 父级消警成功后刷新实时告警（同 App.vue getAlarmData） */
    reloadAfterClearance() {
      this.loadRealtimeAlarms();
    },
  },
};
</script>

<style scoped>
.pv-site-pair {
  font-size: inherit;
  letter-spacing: 0.02em;
}
.pv-num-slash {
  opacity: 0.45;
  padding: 0 0.15em;
  font-weight: normal;
}
.pv-alarm-count-hint {
  color: rgba(255, 255, 255, 0.3);
  font-weight: normal;
  font-size: 10px;
  margin-left: auto;
}
.pv-foot-avg {
  color: #00d4ff;
  font-weight: bold;
}
.pv-foot-max {
  color: #00e676;
}
.pv-foot-min {
  color: #ff5252;
}
</style>
